import React, { useState, useEffect } from 'react';
import { Loader2, RotateCw, Check, AlertTriangle, HelpCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToastUIStore } from '../../store/toastUIStore';
import { logger } from '../../utils/logger';

interface Project {
  id: string;
  name: string;
  task_count: number;
}

interface RebalanceStats {
  topic_id: string;
  topic_name: string;
  status: string;
  before_count: number;
  after_count: number;
  min_pos_before: number;
  max_pos_before: number;
  min_pos_after: number;
  max_pos_after: number;
}

export const TaskManagement: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [topics, setTopics] = useState<{ id: string; name: string }[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [isRebalancing, setIsRebalancing] = useState(false);
  const [lastRebalanced, setLastRebalanced] = useState<string | null>(null);
  const [rebalanceStats, setRebalanceStats] = useState<RebalanceStats[]>([]);
  const [rebalanceAllProjects, setRebalanceAllProjects] = useState<boolean>(false);
  const { addToast } = useToastUIStore();

  // Fetch projects with task counts
  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_projects_with_task_counts');

        if (error) throw error;

        setProjects(data || []);
        if (data && data.length > 0) {
          setSelectedProjectId(data[0].id);
        }
      } catch (error) {
        logger.error('Failed to fetch projects', error);
        addToast({
          type: 'error',
          message: 'Failed to load projects',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
     
  }, []);

  // Fetch topics when a project is selected
  useEffect(() => {
    if (!selectedProjectId) return;

    const fetchTopics = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('topics')
          .select('id, name')
          .eq('project_id', selectedProjectId)
          .eq('topic_type', 'todo'); // Only task topics

        if (error) throw error;

        setTopics(data || []);
        setSelectedTopicId('all'); // Reset to "All Topics"
      } catch (error) {
        logger.error('Failed to fetch topics', error);
        addToast({
          type: 'error',
          message: 'Failed to load topics',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopics();
     
  }, [selectedProjectId]);

  const handleRebalanceTasks = async () => {
    // Check if we have a valid selection - either a specific project or all projects
    if (!selectedProjectId && !rebalanceAllProjects) {
      addToast({
        type: 'error',
        message: 'Please select a project or choose to rebalance all projects',
      });
      return;
    }

    setIsRebalancing(true);
    setRebalanceStats([]);

    try {
      if (rebalanceAllProjects) {
        // Rebalancing ALL projects
        logger.component('Starting global task position rebalancing for ALL projects');

        const { data, error } = await supabase.rpc('rebalance_all_projects_tasks', {
          p_position_step: 10000, // Use large gaps between positions
        });

        if (error) throw error;

        setLastRebalanced(new Date().toISOString());

        addToast({
          type: 'success',
          message: `Successfully rebalanced all tasks across ${data.projects_count} projects (${data.tasks_count} tasks)`,
        });

        logger.component('Global task rebalancing completed', {
          projectsCount: data.projects_count,
          tasksCount: data.tasks_count,
        });
      } else {
        // Rebalancing a specific project (and potentially a specific topic)
        logger.component('Starting task position rebalancing', {
          projectId: selectedProjectId,
          topicId: selectedTopicId,
        });

        // Get the current state of tasks for comparison
        const beforeStats = await getTaskPositionStats();

        // Call the rebalance function
        const { data, error } = await supabase.rpc('rebalance_project_task_positions', {
          p_project_id: selectedProjectId,
          p_topic_id: selectedTopicId === 'all' ? null : selectedTopicId,
          p_position_step: 10000, // Use large gaps between positions
        });

        if (error) throw error;

        // Get the updated state of tasks after rebalancing
        const afterStats = await getTaskPositionStats();

        // Generate comparison stats
        const stats: RebalanceStats[] = beforeStats.map(before => {
          const after = afterStats.find(
            a => a.topic_id === before.topic_id && a.status === before.status
          );

          return {
            topic_id: before.topic_id,
            topic_name: before.topic_name,
            status: before.status,
            before_count: before.count,
            after_count: after?.count || 0,
            min_pos_before: before.min_position,
            max_pos_before: before.max_position,
            min_pos_after: after?.min_position || 0,
            max_pos_after: after?.max_position || 0,
          };
        });

        setRebalanceStats(stats);
        setLastRebalanced(new Date().toISOString());

        addToast({
          type: 'success',
          message: `Successfully rebalanced ${data.rebalanced_count} tasks`,
        });

        logger.component('Task position rebalancing completed', {
          rebalancedCount: data.rebalanced_count,
          stats,
        });
      }
    } catch (error) {
      logger.error('Failed to rebalance task positions', error);
      addToast({
        type: 'error',
        message: 'Failed to rebalance task positions',
      });
    } finally {
      setIsRebalancing(false);
    }
  };

  // Helper function to get task position statistics
  const getTaskPositionStats = async () => {
    try {
      // If we're in "all projects" mode, we need a different approach to get stats
      if (rebalanceAllProjects) {
        // For all projects mode, we'd need a separate endpoint or a different approach
        // This could be implemented with a custom RPC function if needed
        return [];
      } else {
        // For specific project
        const { data, error } = await supabase.rpc('get_task_position_stats', {
          p_project_id: selectedProjectId,
          p_topic_id: selectedTopicId === 'all' ? null : selectedTopicId,
        });

        if (error) throw error;
        return data || [];
      }
    } catch (error) {
      logger.error('Failed to get task position stats', error);
      return [];
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border border-gray-700 mb-6">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Task Position Management</h2>

        <div className="mb-6 bg-blue-900/20 border border-blue-500 text-blue-400 p-4 rounded-lg text-sm">
          <div className="flex items-start">
            <HelpCircle size={20} className="flex-shrink-0 mr-2 mt-0.5" />
            <div>
              <p className="font-medium mb-1">About Task Position Rebalancing</p>
              <p>
                This tool rebalances task positions using a sparse numbering system (steps of
                10,000) to ensure consistent ordering in task boards.
              </p>
              <p className="mt-2">Use this when:</p>
              <ul className="list-disc ml-6 mt-1">
                <li>Tasks aren't sorting correctly</li>
                <li>New tasks aren't appearing at the top</li>
                <li>Drag and drop reordering is inconsistent</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center mb-2 bg-gray-700/30 p-3 rounded-lg">
            <input
              type="checkbox"
              id="rebalanceAllProjects"
              checked={rebalanceAllProjects}
              onChange={() => setRebalanceAllProjects(!rebalanceAllProjects)}
              disabled={isLoading || isRebalancing}
              className="w-4 h-4 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="rebalanceAllProjects" className="ml-2 text-sm font-medium text-white">
              Rebalance ALL projects and topics (global operation)
            </label>
          </div>

          {rebalanceAllProjects && (
            <div className="bg-yellow-900/20 border border-yellow-500 text-yellow-400 p-3 rounded-lg text-sm mt-2">
              <div className="flex items-start">
                <AlertTriangle size={18} className="flex-shrink-0 mr-2 mt-0.5" />
                <div>
                  <p>
                    <strong>Warning:</strong> This operation will rebalance tasks across ALL
                    projects in the system.
                  </p>
                  <p className="mt-1">
                    This may take a while and could impact database performance temporarily.
                    Consider running this during off-peak hours.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Select Project</label>
            <select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              disabled={isLoading || isRebalancing || rebalanceAllProjects}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="" disabled>
                Select a project
              </option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.task_count} tasks)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Select Topic</label>
            <select
              value={selectedTopicId}
              onChange={e => setSelectedTopicId(e.target.value)}
              disabled={isLoading || isRebalancing || !selectedProjectId || rebalanceAllProjects}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="all">All Task Topics</option>
              {topics.map(topic => (
                <option key={topic.id} value={topic.id}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            {lastRebalanced && (
              <p className="text-sm text-gray-400">
                Last rebalanced: {formatDateTime(lastRebalanced)}
              </p>
            )}
          </div>

          <button
            onClick={handleRebalanceTasks}
            disabled={isRebalancing || (!selectedProjectId && !rebalanceAllProjects)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRebalancing ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Rebalancing...
              </>
            ) : (
              <>
                <RotateCw className="mr-2" size={16} />
                {rebalanceAllProjects ? 'Rebalance ALL Projects' : 'Rebalance Task Positions'}
              </>
            )}
          </button>
        </div>

        {rebalanceStats.length > 0 && (
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <h3 className="font-medium text-white mb-3 flex items-center">
              <Check className="text-green-500 mr-2" size={18} />
              Rebalancing Results
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-400 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-2 text-left">Topic</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-right">Count</th>
                    <th className="px-4 py-2 text-right">Before Range</th>
                    <th className="px-4 py-2 text-right">After Range</th>
                  </tr>
                </thead>
                <tbody>
                  {rebalanceStats.map((stat, index) => (
                    <tr key={index} className="border-t border-gray-800">
                      <td className="px-4 py-2 text-white">{stat.topic_name}</td>
                      <td className="px-4 py-2 text-gray-300">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs ${
                            stat.status === 'todo'
                              ? 'bg-blue-900/30 text-blue-400'
                              : stat.status === 'doing'
                                ? 'bg-yellow-900/30 text-yellow-400'
                                : 'bg-green-900/30 text-green-400'
                          }`}
                        >
                          {stat.status.charAt(0).toUpperCase() + stat.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-white">
                        {stat.after_count}
                        {stat.before_count !== stat.after_count && (
                          <span className="ml-1 text-red-400">
                            (
                            {stat.before_count !== stat.after_count
                              ? `${stat.before_count} → ${stat.after_count}`
                              : stat.before_count}
                            )
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-400">
                        {stat.min_pos_before} to {stat.max_pos_before}
                      </td>
                      <td className="px-4 py-2 text-right text-green-400">
                        {stat.min_pos_after} to {stat.max_pos_after}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
